import React from 'react';
import { motion } from 'framer-motion';
import { FiBriefcase, FiBook, FiExternalLink } from 'react-icons/fi';
import RevealHeading from '../common/RevealHeading';
import useParallax from '../common/useParallax';

const ExperienceTimeline = () => {
  const { ref: parallaxRef, y: parallaxY } = useParallax(60);
  const experiences = [
    {
      type: 'work',
      icon: FiBriefcase,
      title: 'CTO & Co-Founder',
      company: 'Zynqora Edge',
      companyUrl: 'https://zynqoraedge.com',
      period: '2024 - Present',
      description: 'Leading technical direction and product development for an AI-focused startup. Managing architecture decisions, AI systems, and engineering workflows.',
      details: ['Technical Leadership', 'Product Strategy', 'AI Development', 'Team Management'],
    },
    {
      type: 'work',
      icon: FiBriefcase,
      title: 'ML Engineer Intern',
      company: 'Developers Hub',
      companyUrl: null,
      period: 'Feb - May 2024',
      description: 'Trained and fine-tuned ML models for classification tasks. Built NLP pipelines and worked on image classification systems using CNNs and transfer learning.',
      details: ['Model Training', 'NLP Pipelines', 'Computer Vision', 'Data Processing'],
    },
    {
      type: 'work',
      icon: FiBriefcase,
      title: 'Freelance Developer',
      company: 'Upwork & Fiverr',
      companyUrl: 'https://www.upwork.com/freelancers/~016b95942fb99caccf',
      period: '2021 - Present',
      description: 'Delivering web and mobile applications for international clients. Specializing in React, Node.js, Flutter, and AI integration projects.',
      details: ['React.js', 'Node.js', 'Flutter', 'AI Integration'],
    },
    {
      type: 'education',
      icon: FiBook,
      title: 'BS Artificial Intelligence',
      company: 'Barani Institute of IT (PMAS)',
      companyUrl: null,
      period: '2022 - 2026',
      description: 'Specialized degree in Artificial Intelligence covering machine learning, deep learning, computer vision, and NLP.',
      details: ['Machine Learning', 'Deep Learning', 'Computer Vision', 'NLP'],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  return (
    <section id="experience" ref={parallaxRef} className="py-20 lg:py-28 relative overflow-hidden">
      {/* Background */}
      <motion.div
        style={{ y: parallaxY }}
        animate={{ opacity: [0.1, 0.3, 0.1] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-1/2 left-0 w-96 h-96 bg-primary-500 rounded-full blur-3xl opacity-5"
      />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <RevealHeading text="Experience &" accent="Education" className="text-4xl lg:text-5xl font-bold text-white mb-4" />
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            My professional background and academic foundation
          </p>
        </motion.div>

        {/* Timeline */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          {experiences.map((exp, idx) => {
            const Icon = exp.icon;
            const isWork = exp.type === 'work';
            const isEducation = exp.type === 'education';

            return (
              <motion.div key={idx} variants={itemVariants} className="mb-8 lg:mb-12">
                <div className="flex gap-6 lg:gap-8">
                  {/* Timeline Dot & Line */}
                  <div className="flex flex-col items-center">
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                        isWork
                          ? 'bg-gradient-to-br from-primary-500 to-accent-500'
                          : isEducation
                          ? 'bg-gradient-to-br from-accent-500 to-primary-500'
                          : 'bg-gradient-to-br from-primary-500 via-accent-500 to-primary-500'
                      }`}
                    >
                      <Icon className="text-white text-lg" />
                    </motion.div>
                    {idx !== experiences.length - 1 && (
                      <div className="w-1 h-20 lg:h-32 bg-gradient-to-b from-primary-500/50 to-transparent" />
                    )}
                  </div>

                  {/* Content */}
                  <motion.div
                    whileHover={{ x: 10 }}
                    className="flex-1 pt-2 cursor-pointer group"
                  >
                    <div className="p-6 rounded-xl bg-dark-800/30 border border-primary-500/10 group-hover:border-primary-500/30 transition-all duration-300 backdrop-blur-sm">
                      {/* Period Badge */}
                      <div className="inline-block mb-2 px-3 py-1 rounded-full bg-primary-500/20 border border-primary-500/30">
                        <span className="text-xs font-semibold text-primary-400">{exp.period}</span>
                      </div>

                      {/* Title & Company */}
                      <h3 className="text-xl lg:text-2xl font-bold text-white mb-1 group-hover:text-primary-500 transition-colors">
                        {exp.title}
                      </h3>
                      {exp.companyUrl ? (
                        <a
                          href={exp.companyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-accent-500 font-semibold mb-3 hover:text-primary-500 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {exp.company}
                          <FiExternalLink className="text-sm" />
                        </a>
                      ) : (
                        <p className="text-accent-500 font-semibold mb-3">{exp.company}</p>
                      )}

                      {/* Description */}
                      <p className="text-white/70 mb-4 leading-relaxed">{exp.description}</p>

                      {/* Details Tags */}
                      <div className="flex flex-wrap gap-2">
                        {exp.details.map((detail, detailIdx) => (
                          <motion.span
                            key={detailIdx}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ delay: detailIdx * 0.05 }}
                            className="px-3 py-1 rounded-full bg-accent-500/10 text-accent-400 text-sm font-medium border border-accent-500/20"
                          >
                            {detail}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ExperienceTimeline;
